import { VehicleInformation } from '../../models/entities/vehicle-information.model';
import GenericReceivedObject from '../../models/utilities/generic-received-object.model';
import { useStore } from '../../composables/store.composable';

import {
  Maybe,
  Model,
  Motorisation,
  TestDriveVehicle,
} from '../../graphql/schema';

import UtilsService from '../utils.service';
import { i18n } from '../translation.service';
import {
  CONTEXT_OPTIONS,
  WEIGHT_CATEGORY,
} from '../../../../otbc/src/models/constants';

export default class VehicleInformationService {
  static getMotorisationLabel(name: string, engine: any): string {
    let label = name;
    if (engine?.powerInKw?.value && engine?.powerInHp?.value) {
      const powerInKwValueFormatted =
        VehicleInformationService.removeDecimalDigits(engine.powerInKw?.value);
      const powerInHpValueFormatted =
        VehicleInformationService.removeDecimalDigits(engine.powerInHp?.value);
      label += `, ${powerInKwValueFormatted} ${engine.powerInKw?.unit} (${powerInHpValueFormatted} ${engine.powerInHp?.unit})`;
    }
    return label;
  }

  static removeDecimalDigits(value: string): string {
    const pattern = /^(\d+)/;
    const formattedValue: RegExpMatchArray | null = value.match(pattern);
    return formattedValue ? formattedValue[0] : value;
  }

  static getMaxGrossWeightValue(
    vehicle: any,
    motorisation?: Motorisation
  ): string {
    const maximumWeight =
      vehicle?.weight?.maximum || motorisation?.weight?.maximum;
    if (!maximumWeight) {
      // For showing the selected option by the user in request mode, if it exists
      return vehicle.weight;
    }

    const weightObj = maximumWeight;
    const firstKey = Object.keys(weightObj)[0];
    let result;
    if (weightObj[firstKey] && weightObj[firstKey].value) {
      const weightValue = weightObj[firstKey].value;
      const weightInTons = weightValue / 1000;
      result = weightInTons + ' t';
    }

    return result || '';
  }

  static getVehicleInformation(
    vehicle: TestDriveVehicle,
    canShowDefaultData = true
  ): VehicleInformation {
    const { commit, state, getter } = useStore();
    const vehicleInformation: VehicleInformation = {
      image: {
        url:
          vehicle.images?.fallbackImages?.MEDIUM ||
          vehicle.images?.defaultImage,
        title: vehicle.name,
      },
      primary: [],
      secondary: [],
    };
    let motorisation: Motorisation | undefined;
    if (
      !vehicle.fuelType ||
      !vehicle.transmission ||
      !vehicle?.engine?.powerInKw?.value
    ) {
      motorisation = state.otbcFlow.selectedModelMotorisations.find(
        (motorisation) => motorisation.id === vehicle.motorisationId
      );
    }
    if (!motorisation) {
      motorisation = state.otbcFlow.confirmedMotorisation;
    }
    const showDefaultData =
      !!state.otbcFlow.confirmedMotorisation ||
      (!!state.otbcFlow.filteredBookingsSearchResults.length &&
        canShowDefaultData);

    const fuelType =
      vehicle.fuelType ||
      motorisation?.fuelType ||
      state.otbcFlow.confirmedFuelType;

    if (fuelType || showDefaultData) {
      vehicleInformation.primary.push({
        key: '',
        value: fuelType
          ? `${i18n.t('shared.fuel.' + fuelType.toLowerCase())}`
          : '-',
        order: 1,
        icon: 'bds/fuel/24',
        tooltipText: '',
        class: null,
        distance: null,
        unit: null,
      });
    }

    const transmission =
      vehicle.transmission ||
      motorisation?.transmissions?.[0] ||
      state.otbcFlow.confirmedTransmission;
    if (transmission || showDefaultData) {
      vehicleInformation.primary.push({
        key: '',
        value: transmission
          ? `${i18n.t('shared.transmission.' + transmission.toLowerCase())}`
          : '-',
        order: 2,
        icon: 'gear',
        tooltipText: '',
        class: null,
        distance: null,
        unit: null,
      });
    }

    const engine = vehicle.engine || motorisation?.engine;
    let engineValue;
    if (engine?.powerInKw?.value && engine?.powerInHp?.value) {
      engineValue = `${engine.powerInKw.value} ${
        engine.powerInKw?.unit ?? ''
      } (${engine.powerInHp.value} ${engine.powerInHp?.unit ?? ''})`;
    }
    if (engineValue || showDefaultData) {
      vehicleInformation.primary.push({
        key: '',
        value: engineValue || '-',
        order: 0,
        icon: 'bds/engine/24',
        tooltipText: '',
        class: 'vehicle-information__summary--motorization-item-engine',
        distance: null,
        unit: null,
      });
    }

    const showFootNotes = state.configuration.footNotes?.visible;

    if (getter?.isCo2EmissionAndFuelConsumptionVisibile)
      vehicleInformation.secondary = [
        ...VehicleInformationService.getVehicleCO2andConsumption(
          vehicle,
          showFootNotes,
          canShowDefaultData
        ),
      ];
    if (state.configuration.context === CONTEXT_OPTIONS.VANS) {
      vehicleInformation.secondary = [
        ...VehicleInformationService.getVansSpecificInformation(
          vehicle,
          canShowDefaultData
        ),
        ...vehicleInformation.secondary,
      ];
    }
    commit('vehiclePreferences', vehicleInformation);
    return vehicleInformation;
  }

  static handleReceivedWheelBase(wheelBase: any): string {
    return wheelBase?.name || wheelBase;
  }

  static getVansSpecificInformation(
    vehicle: any,
    canShowDefaultData = true
  ): GenericReceivedObject[] {
    const vansInformation: GenericReceivedObject[] = [];
    const { state } = useStore();
    let motorisation: Motorisation | undefined;
    if (!vehicle.driveType || !vehicle.wheelBase || !vehicle.weight) {
      motorisation = state.otbcFlow.selectedModelMotorisations.find(
        (motorisation) => motorisation.id === vehicle.motorisationId
      );
    }
    const showDefaultData =
      !!state.otbcFlow.confirmedMotorisation ||
      (!!state.otbcFlow.filteredBookingsSearchResults.length &&
        canShowDefaultData);

    const driveType = vehicle.driveType || motorisation?.driveType;
    if (driveType || showDefaultData) {
      vansInformation.push({
        key: `${i18n.t('otbs.vehicleInformation.driveType')}:`,
        value: driveType || '-',
        order: 0,
        icon: '',
        tooltipText: '',
        class: null,
        distance: null,
        unit: null,
      });
    }

    const wheelBase = vehicle.wheelBase || motorisation?.wheelBase;
    if (wheelBase || showDefaultData) {
      vansInformation.push({
        key: `${i18n.t('otbs.vehicleInformation.wheelBase')}:`,
        value: wheelBase
          ? VehicleInformationService.handleReceivedWheelBase(wheelBase)
          : '-',
        order: 0,
        icon: '',
        tooltipText: '',
        class: null,
        distance: null,
        unit: null,
      });
    }

    const weight = vehicle?.weight || motorisation?.weight;
    const weightCategory =
      vehicle?.weightCategory || motorisation?.weightCategory;
    if (weightCategory || showDefaultData) {
      vansInformation.push({
        key: `${i18n.t('otbs.vehicleInformation.weight')}:`,
        value: weight
          ? `${VehicleInformationService.getMaxGrossWeightValue(
              vehicle,
              motorisation
            )}`
          : '-',
        order: 0,
        icon:
          weightCategory === WEIGHT_CATEGORY.HEAVY_WEIGHT
            ? 'bds/info-circle/16'
            : '',
        tooltipText:
          weightCategory === WEIGHT_CATEGORY.HEAVY_WEIGHT
            ? `${i18n.t('schedule.booking.vehicleTile.weight.tooltip')}`
            : '',
        class: null,
        distance: null,
        unit: null,
      });
    }
    return vansInformation;
  }

  static getVehicleCO2andConsumption(
    vehicle: TestDriveVehicle,
    showFootNotes = false,
    canShowDefaultData = true
  ): GenericReceivedObject[] {
    const vehicleInformation: GenericReceivedObject[] = [];
    const { state } = useStore();
    let motorisation: Motorisation | undefined;
    if (
      !vehicle.co2Emission ||
      !vehicle.fuelConsumption ||
      !vehicle.energyEfficiencyClass
    ) {
      motorisation = state.otbcFlow.selectedModelMotorisations.find(
        (motorisation) => motorisation.id === vehicle.motorisationId
      );
    }
    if (!motorisation) {
      motorisation = state.otbcFlow.selectedMotorisation;
    }
    const showDefaultData =
      !!state.otbcFlow.confirmedMotorisation ||
      (!!state.otbcFlow.filteredBookingsSearchResults.length &&
        canShowDefaultData);
    const co2Emission = vehicle.co2Emission || motorisation?.co2Emission;
    if (co2Emission || showDefaultData) {
      vehicleInformation.push({
        key: `${i18n.t('otbs.vehicleInformation.co2')}:`,
        value: co2Emission
          ? UtilsService.formatTechnicalRangeMetric(co2Emission)
          : '-',
        order: 0,
        icon: '',
        tooltipText: '',
        class: null,
        distance: null,
        showFootNotes,
        unit: null,
      });
    }

    const fuelConsumption =
      vehicle.fuelConsumption || motorisation?.fuelConsumption;
    if (fuelConsumption || showDefaultData) {
      const fuelConsumptionValue = fuelConsumption
        ? UtilsService.formatTechnicalRangeMetric(
            fuelConsumption.electric || fuelConsumption.fossil
          )
        : '-';
      vehicleInformation.push({
        key: `${i18n.t('otbs.vehicleInformation.fuelConsumption')}:`,
        value: fuelConsumptionValue,
        order: 1,
        icon: '',
        tooltipText: '',
        class: null,
        distance: null,
        unit: null,
        showFootNotes,
      });
    }

    const energyEfficiencyClass =
      vehicle.energyEfficiencyClass || motorisation?.energyEfficiencyClass;
    if (energyEfficiencyClass || showDefaultData) {
      vehicleInformation.push({
        key: `${i18n.t('otbs.vehicleInformation.efficiency')}:`,
        value: energyEfficiencyClass?.value || '-',
        order: 2,
        icon: '',
        tooltipText: '',
        class: null,
        distance: null,
        unit: null,
        showFootNotes,
      });
    }

    return vehicleInformation;
  }

  static getModelInformation(
    model: Model,
    transmission: Maybe<string>,
    fuelType: Maybe<string>,
    motorisation: Maybe<Motorisation>
  ): VehicleInformation {
    const modelInformation: VehicleInformation = {
      image: {
        url: model.images?.fallbackImages.MEDIUM,
        title: model.name,
      },
      primary: [],
      secondary: [],
    };

    if (transmission) {
      modelInformation.primary.push({
        key: '',
        value: `${i18n.t('shared.transmission.' + transmission.toLowerCase())}`,
        order: 1,
        icon: 'gear-xs',
        tooltipText: '',
        class: null,
        distance: null,
        unit: null,
      });
    }

    if (fuelType) {
      modelInformation.primary.push({
        key: '',
        value: `${i18n.t('shared.fuel.' + fuelType.toLowerCase())}`,
        order: 2,
        icon: 'bds/fuel/24',
        tooltipText: '',
        class: null,
        distance: null,
        unit: null,
      });
    }

    if (motorisation) {
      if (motorisation?.engine) {
        modelInformation.primary.push({
          key: '',
          value: `${motorisation.engine.powerInKw?.value} ${motorisation.engine.powerInKw?.unit} (${motorisation.engine.powerInHp?.value} ${motorisation.engine.powerInHp?.unit})`,
          order: 0,
          icon: 'bds/engine/24',
          tooltipText: '',
          class: 'vehicle-information__summary--motorization-item-engine',
          distance: null,
          unit: null,
        });
      }

      if (motorisation.co2Emission) {
        modelInformation.secondary.push({
          key: `${i18n.t('otbs.vehicleInformation.co2')}:`,
          value: `${UtilsService.formatTechnicalRangeMetric(
            motorisation.co2Emission
          )}`,
          order: 0,
          icon: 'consumption',
          tooltipText: '',
          class: null,
          distance: null,
          unit: null,
        });
      }

      if (motorisation.fuelConsumption) {
        modelInformation.secondary.push({
          key: `${i18n.t('otbs.vehicleInformation.fuelConsumption')}:`,
          value: `${UtilsService.formatTechnicalRangeMetric(
            motorisation.fuelConsumption.electric ||
              motorisation.fuelConsumption.fossil
          )}`,
          order: 1,
          icon: 'bds/fuel/24',
          tooltipText: '',
          class: null,
          distance: null,
          unit: null,
        });
      }

      if (motorisation.energyEfficiencyClass) {
        modelInformation.secondary.push({
          key: `${i18n.t('otbs.vehicleInformation.efficiency')}:`,
          value: motorisation.energyEfficiencyClass.value,
          order: 2,
          icon: 'mileage',
          tooltipText: '',
          class: null,
          distance: null,
          unit: null,
        });
      }
    }

    return modelInformation;
  }
}
